import { PhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { useMemo } from "react";
import * as yup from "yup";
import { Employee } from "../models";

// NOTE: Phone number validation also exists in the my portal
// Any changes here should be reflected there too...

export const isMobileNumber = (
  phoneNumber: PhoneNumber,
  employeeCountryCode: string
) => {
  if (phoneNumber.country !== employeeCountryCode) return false; // If the derived phone numbers country code doesn't match the intended (employers)

  // Check the phone number matches the derived country format (eg. if its +64, next should be 2 for NZ)
  switch (phoneNumber.country) {
    case "NZ":
      return phoneNumber.nationalNumber.startsWith("2");
    case "AU":
      return (
        phoneNumber.nationalNumber.startsWith("4") ||
        phoneNumber.nationalNumber.startsWith("5")
      );
    default:
      return false;
  }
};

// Used only in Customer Portal, as we allow email address to change here.
// Not mirrored in the My Portal, as no address editing supported there.
export const isEmployeeEmailUnique = (
  allEmployees: Employee[],
  email: string
) => {
  const foundEmployeeWithEmail = allEmployees.find(
    (e) => e.contact.email === email
  );

  if (!foundEmployeeWithEmail) return true;
  return false;
};

export const useDynamicEmployeeValidationSchema = (
  employeeCountryCode: any,
  allEmployees: Employee[] | undefined
) => {
  return useMemo(() => {
    const phoneNumberValidation = yup
      .string()
      .nullable() // Allows null values
      .test(
        "is-valid-mobile-phone",
        `Invalid ${employeeCountryCode} mobile phone number`,
        function (value) {
          if (!value) return true; // Allow empty values

          try {
            const phoneNumber: PhoneNumber | undefined =
              parsePhoneNumberFromString(value, employeeCountryCode);
            if (!phoneNumber) {
              return false;
            }

            //const numberType = getNumberType(phoneNumber.nationalNumber, countryCode); -> doesn't work for some reason, tried with custom metadata (check project.json scripts)
            return (
              phoneNumber.isValid() &&
              isMobileNumber(phoneNumber, employeeCountryCode)
            );
          } catch (error) {
            return this.createError({
              message: `Invalid phone number for country: ${employeeCountryCode}`
            });
          }
        }
      );

    const emailValidation = yup
      .string()
      .email("Invalid email")
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
      .test(
        "not-invalid",
        "Email cannot contain special characters in invalid positions",
        (value) => !/^\S+\*|\S+@-/.test(value ?? "")
      )
      .test("unique", "Email already in use", function (value) {
        if (!value || !allEmployees) return true; // Allow empty values
        return isEmployeeEmailUnique(allEmployees, value);
      })
      .required("Email is required");

    return yup.object().shape({
      firstName: yup.string().max(100).required(),
      lastName: yup.string().max(100).required(),
      phone: phoneNumberValidation,
      email: emailValidation
    });
  }, [allEmployees, employeeCountryCode]);
};

export const useDynamicEmployerValidationSchema = (
  employerCountryCode: any
) => {
  return useMemo(() => {
    const phoneNumberValidation = yup
      .string()
      .nullable() // Allows null values
      .test(
        "is-valid-mobile-phone",
        `Invalid ${employerCountryCode} mobile phone number`,
        function (value) {
          if (!value) return true; // Allow empty values
          try {
            const phoneNumber: PhoneNumber | undefined =
              parsePhoneNumberFromString(value, employerCountryCode);
            if (!phoneNumber) {
              return false;
            }

            //const numberType = getNumberType(phoneNumber.nationalNumber, countryCode); -> doesn't work for some reason, tried with custom metadata (check project.json scripts)
            return (
              phoneNumber.isValid() &&
              isMobileNumber(phoneNumber, employerCountryCode)
            );
          } catch (error) {
            return this.createError({
              message: `Invalid phone number for country: ${employerCountryCode}`
            });
          }
        }
      );

    return yup.object().shape({
      contactFirstName: yup.string().max(100).required(),
      contactLastName: yup.string().max(100).required(),
      name: yup.string().max(100).required(),
      contact: yup.object().shape({
        phone: phoneNumberValidation,
        email: yup
          .string()
          .email("Invalid email")
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email") // Checks email domain (eg. example@google.com VALID, example@google INVALID)
          .test(
            "not-invalid",
            "Invalid email",
            (value) => !/^\S+\*|\S+@-/.test(value ?? "")
          )
          .required(),
        address: yup.object().shape({
          address1: yup.string().max(100).required(),
          address2: yup.string().max(100),
          city: yup.string().max(100).required(),
          postalCode: yup.number().required(),
          country: yup.string().max(100).required()
        })
      })
    });
  }, [employerCountryCode]);
};
